import type { RouteLocation } from 'vue-router';
import type { AgentTools } from '../runtime/types/tools';
import { setAgentToolsRegistry } from '../runtime/utils/agentTools';
import { AgentToolsRegistry } from '../runtime/utils/agentToolsRegistry';

export default defineNuxtPlugin({
    name: 'tools',
    dependsOn: ['nuxt:router'],
    async setup()
    {
        const { $router } = useNuxtApp();
        const store = useAuthStore();
        const registry = new AgentToolsRegistry(() => ({ loaded: store.loaded, authenticated: store.authenticated }));

        // Zmiana widoku (nazwy trasy) czyści poziom ulotny - parametry/query tej samej trasy nie liczą
        // się jako zmiana widoku, tak samo jak w guard.ts
        $router.afterEach((to: RouteLocation, from: RouteLocation) =>
        {
            if (to.name != from?.name)
            {
                registry.clearEphemeral();
            }
        });

        // Wylogowanie w SPA nie przeładowuje strony, a token potrafi wygasnąć sam bez wywołania akcji
        // wylogowania - sygnałem jest więc przejście stanu sesji z zalogowanej na niezalogowaną
        watch(() => store.authenticated, (value, previous) =>
        {
            if (previous === true && value === false)
            {
                registry.clearEphemeral();
            }
        });

        // Obiekt zawężony do pięciu metod kontraktu, a nie instancja rejestru - sam interfejs zawęża
        // tylko typ, więc clearEphemeral byłoby wołalne z konsoli przeglądarki. Ten obiekt idzie na
        // window, z którego korzysta wołający spoza aplikacji
        const contract: AgentTools = {
            registerTool: registry.registerTool.bind(registry),
            registerPrompt: registry.registerPrompt.bind(registry),
            registerResource: registry.registerResource.bind(registry),
            list: registry.list.bind(registry),
            call: registry.call.bind(registry)
        };

        // Poza warunkiem na window, bo kod panelu rejestruje się przez moduł i nie potrzebuje DOM -
        // pod warunkiem brak window znów po cichu gubiłby rejestracje panelu
        //
        // Moduł dostaje kontrakt poszerzony o wyrejestrowanie, a window sam kontrakt: wyrejestrowanie
        // służy kodowi panelu, który zdejmuje własną rejestrację, gdy jego element znika w obrębie
        // jednego widoku. Wołający spoza aplikacji zdejmowałby nim rejestracje widoku, którego nie
        // kontroluje - tak samo jak czyszczeniem całego poziomu ulotnego
        setAgentToolsRegistry({ ...contract, unregister: registry.unregister.bind(registry) });

        // Wołający spoza aplikacji (agent sterujący przeglądarką) potrzebuje realnego window; SPA bez
        // SSR sprawia, że w panelu warunek jest zawsze prawdziwy, ale bez niego montowanie wysypywałoby
        // się w środowisku bez DOM
        if (typeof window !== 'undefined')
        {
            window.edito = window.edito || {};
            window.edito.tools = contract;
        }
    }
});

declare global
{
    interface Window
    {
        edito?: {
            tools?: AgentTools;
        };
    }
}
